import React, {
	forwardRef, useEffect, useMemo, useRef, useState
} from 'react'
import {
	Button, Checkbox, Col, Empty, Modal, Row, Table
} from 'antd'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import { ColumnsType } from 'antd/lib/table'
import { useReactToPrint } from 'react-to-print'
import { IJobFirmHeader } from '../../../reducers/jobs/actions'
import {
	renderOrganizationTag, renderQrScanDuration, renderQrScanLastService, renderTrustees
} from '../../jobs/components/jobColumnRenderers'
import {
	ISuperOrganizationJobsParams, SuperOrganizationJobTableItem, useSuperOrganizationJobs
} from '../../../queries/superOrganization/useSuperOrganizationJobs'

export const PRINT_COLUMNS_STORAGE_KEY = 'umyjeme:superOrganizationPrintColumns'

// Katalog je jediny zdroj pravdy - pouziva sa aj pre checkboxy aj pre tlacenu tabulku.
interface IPrintColumn {
	key: string
	title: string
	defaultChecked: boolean
	column: ColumnsType<SuperOrganizationJobTableItem>[number]
}

// Katalog sa musi zostavovat az pri renderi - i18next nacitava slovniky asynchronne,
// pri evaluacii modulu by boli vsetky titulky prazdne (checkboxy aj hlavicky tabulky bez popisu).
const getPrintColumns = (): IPrintColumn[] => [
	{
		key: 'address',
		title: i18next.t('pages:Adresa'),
		defaultChecked: true,
		column: {
			title: i18next.t('pages:Adresa'),
			dataIndex: 'address',
			key: 'address',
			render: (value) => value || '-'
		}
	},
	{
		key: 'organization',
		title: i18next.t('pages:Organizácia'),
		defaultChecked: true,
		column: {
			title: i18next.t('pages:Organizácia'),
			dataIndex: 'organization',
			key: 'organization',
			render: renderOrganizationTag
		}
	},
	{
		key: 'trustees',
		title: i18next.t('pages:Zodpovedné osoby'),
		defaultChecked: true,
		column: {
			title: i18next.t('pages:Zodpovedné osoby'),
			dataIndex: 'trustees',
			key: 'trustees',
			render: renderTrustees
		}
	},
	{
		key: 'firmHeader',
		title: i18next.t('pages:s.r.o.'),
		defaultChecked: true,
		column: {
			title: i18next.t('pages:s.r.o.'),
			dataIndex: 'firmHeader',
			key: 'firmHeader',
			render: (value: IJobFirmHeader | null) => value?.firmName || '-'
		}
	},
	{
		key: 'qrScanDuration',
		title: i18next.t('pages:Trvanie'),
		defaultChecked: false,
		column: {
			title: i18next.t('pages:Trvanie'),
			dataIndex: 'qrScan',
			key: 'qrScanDuration',
			align: 'center',
			render: renderQrScanDuration
		}
	},
	{
		key: 'qrScanLastService',
		title: i18next.t('pages:Posledná služba'),
		defaultChecked: false,
		column: {
			title: i18next.t('pages:Posledná služba'),
			dataIndex: 'qrScan',
			key: 'qrScanLastService',
			align: 'right',
			render: renderQrScanLastService
		}
	}
]

const getDefaultSelected = () => getPrintColumns().filter((item) => item.defaultChecked).map((item) => item.key)

const readStoredColumns = (): string[] => {
	const defaultSelected = getDefaultSelected()
	try {
		const raw = window.localStorage.getItem(PRINT_COLUMNS_STORAGE_KEY)
		if (!raw) {
			return defaultSelected
		}
		const parsed = JSON.parse(raw)
		if (!Array.isArray(parsed)) {
			return defaultSelected
		}
		// zahod neznama kluce - katalog sa moze casom zmenit
		const valid = parsed.filter((key: string) => getPrintColumns().some((item) => item.key === key))
		return valid.length > 0 ? valid : defaultSelected
	} catch (error) {
		return defaultSelected
	}
}

const writeStoredColumns = (keys: string[]) => {
	try {
		window.localStorage.setItem(PRINT_COLUMNS_STORAGE_KEY, JSON.stringify(keys))
	} catch (error) {
		// localStorage nemusi byt dostupny
	}
}

// Vlastny tlacovy wrapper namiesto TableToPrint - ten zabaluje obsah do <thead>,
// co je pri antd tabulke neplatne HTML a pridava prazdny canvas z QR kodov.
const PrintableArea = forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children }, ref) => (
	<div ref={ref}>
		<style type={'text/css'} media={'print'}>
			{'@page { size: A4 landscape; margin: 10mm; }'}
		</style>
		{children}
	</div>
))

PrintableArea.displayName = 'PrintableArea'

interface Props {
	visible: boolean
	onClose: () => void
	queryParams: Omit<ISuperOrganizationJobsParams, 'limit' | 'page'>
}

const PrintColumnsModal = ({ visible, onClose, queryParams }: Props) => {
	const { t } = useTranslation('pages')

	const [selectedKeys, setSelectedKeys] = useState<string[]>(readStoredColumns)
	const printRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		writeStoredColumns(selectedKeys)
	}, [selectedKeys])

	// data sa tahaju az po otvoreni modalu, a to cely filtrovany set (nie len viditelna stranka)
	const { data, isFetching } = useSuperOrganizationJobs({
		...queryParams,
		limit: 100000,
		page: 1
	}, { enabled: visible })

	const jobs = data?.jobs || []
	const totalCount = data?.pagination?.totalCount || jobs.length

	const printColumns = getPrintColumns()

	const columns = useMemo(
		() => getPrintColumns().filter((item) => selectedKeys.includes(item.key)).map((item) => item.column),
		[selectedKeys]
	)

	const handlePrint = useReactToPrint({
		content: () => printRef.current
	})

	const hasSelection = selectedKeys.length > 0

	return (
		<Modal
			title={t('pages:Tlačiť zoznam zákaziek')}
			centered
			visible={visible}
			onCancel={onClose}
			width={1000}
			footer={[
				<Button key={'cancel'} onClick={onClose}>{t('pages:Zrušiť')}</Button>,
				<Button
					key={'print'}
					type={'primary'}
					disabled={!hasSelection || isFetching}
					loading={isFetching}
					onClick={() => handlePrint && handlePrint()}
				>
					{t('pages:Vytlačiť')}
				</Button>
			]}
		>
			<Checkbox.Group
				value={selectedKeys}
				onChange={(values) => setSelectedKeys(values as string[])}
				style={{ width: '100%' }}
			>
				<Row gutter={[16, 8]}>
					{printColumns.map((item) => (
						<Col span={12} key={item.key}>
							<Checkbox value={item.key}>{item.title}</Checkbox>
						</Col>
					))}
				</Row>
			</Checkbox.Group>

			<div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'center' }}>
				<Button type={'link'} size={'small'} style={{ padding: 0 }} onClick={() => setSelectedKeys(printColumns.map((item) => item.key))}>
					{t('pages:Označiť všetko')}
				</Button>
				<Button type={'link'} size={'small'} style={{ padding: 0 }} onClick={() => setSelectedKeys([])}>
					{t('pages:Zrušiť výber')}
				</Button>
			</div>

			{!hasSelection && (
				<div style={{ color: '#cf1322', marginTop: 8 }}>{t('pages:Vyberte aspoň jeden stĺpec')}</div>
			)}

			<div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e8e8e8', color: 'rgba(0, 0, 0, 0.45)' }}>
				{t('pages:Vytlačí sa')} <b style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{totalCount}</b> {t('pages:zákaziek podľa aktuálneho filtra')}
			</div>

			<div style={{ marginTop: 12, maxHeight: 360, overflow: 'auto' }}>
				<PrintableArea ref={printRef}>
					<Table
						className={'general-table'}
						columns={columns}
						dataSource={jobs}
						pagination={false}
						showSorterTooltip={false}
						loading={isFetching}
						locale={{ emptyText: <Empty description={t('pages:Žiadne dáta')} /> }}
						size={'small'}
					/>
				</PrintableArea>
			</div>
		</Modal>
	)
}

export default PrintColumnsModal
